import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Send, CheckCircle, AlertCircle, Sparkles, Clock, Code2, Globe } from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

const Contact: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [formData, setFormData] = useState<FormData>({ name: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [focused, setFocused] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const GOOGLE_SCRIPT_URL =
      'https://script.google.com/macros/s/AKfycbwT06_AM7sRbUc1bNB-ThLhX_47XzbRi2VEadJphk1Z7Pa32vZW_aN72RBkBazWYS0v/exec';

    const formDataToSend = new URLSearchParams();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('phone', formData.phone);
    formDataToSend.append('message', formData.message);
    formDataToSend.append('timestamp', new Date().toISOString());

    try {
      fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataToSend.toString(),
      }).catch(() => {});

      setTimeout(() => {
        setIsSubmitting(false);
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setSubmitStatus(null), 5000);
      }, 300);
    } catch (error) {
      setIsSubmitting(false);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  const inputStyle = (fieldName: string) => ({
    width: '100%',
    padding: '14px 18px',
    background: 'rgba(0,0,0,0.02)',
    border: `1px solid ${focused === fieldName ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.08)'}`,
    borderRadius: '16px',
    color: '#0f172a',
    fontSize: '15px',
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    outline: 'none',
    backdropFilter: 'blur(12px)',
    transition: 'all 0.3s ease',
    boxShadow: focused === fieldName ? '0 0 0 4px rgba(0,0,0,0.03)' : 'none',
  });

  const highlights = [
    { icon: <Clock size={18} />, text: 'Quick Response', sub: 'Within 24 hours' },
    { icon: <Code2 size={18} />, text: 'Clean Code', sub: 'Best practices always' },
    { icon: <Globe size={18} />, text: 'Remote Ready', sub: 'Anywhere in the world' },
  ];

  return (
    <section id="contact" className="py-20 lg:py-32 relative overflow-hidden" style={{ background: '#ffffff', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background: 'radial-gradient(circle at 100% 0%, rgba(0,0,0,0.05) 0%, transparent 50%), radial-gradient(circle at 0% 100%, rgba(0,0,0,0.03) 0%, transparent 50%)',
          }}
        />
      </div>

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-14 lg:mb-18"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{
              background: 'rgba(0, 0, 0, 0.03)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Sparkles size={14} className="text-[#475569]" />
            <span className="text-xs font-medium tracking-wide text-[#475569] uppercase">Contact</span>
          </motion.div>

          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4"
            style={{ letterSpacing: '-0.02em', color: '#0f172a' }}
          >
            Get In{' '}
            <span className="text-[#475569]">
              Touch
            </span>
          </h2>

          <p className="text-base sm:text-lg max-w-2xl mx-auto text-[#475569]">
            Have a project in mind? Let's discuss how we can bring your ideas to life.
          </p>
        </motion.div>

        {/* Content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">

          {/* Left: Info panel */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-2 flex flex-col gap-6"
          >
            {/* Ready to connect card */}
            <div
              className="rounded-3xl p-6 sm:p-8 relative overflow-hidden"
              style={{
                background: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                backdropFilter: 'blur(24px)',
                boxShadow: '0 4px 24px -1px rgba(0, 0, 0, 0.05)',
              }}
            >
              <div
                className="absolute inset-0"
                style={{ background: 'radial-gradient(circle at top left, rgba(0,0,0,0.03) 0%, transparent 70%)' }}
              />
              <div className="relative z-10">
                <motion.div
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                  className="mb-6 inline-flex p-3 rounded-2xl"
                  style={{ background: 'rgba(0, 0, 0, 0.04)', border: '1px solid rgba(0, 0, 0, 0.08)' }}
                >
                  <Sparkles className="text-[#0f172a]" size={24} />
                </motion.div>
                <h3
                  className="text-2xl font-bold mb-3 text-[#0f172a]"
                >
                  Ready to Connect?
                </h3>
                <p className="text-sm leading-relaxed mb-6 text-[#475569]">
                  I'm always excited to collaborate on new projects and explore innovative solutions. Share your ideas through the form!
                </p>
                <div className="flex items-center gap-2 text-[#475569]">
                  <Send size={14} />
                  <span className="text-xs font-semibold tracking-wide uppercase">
                    Quick Response Guaranteed
                  </span>
                </div>
              </div>
            </div>

            {/* Highlights */}
            <div className="flex flex-col gap-4">
              {highlights.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.4 + i * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  className="flex items-center gap-5 px-6 py-5 rounded-2xl transition-all duration-300"
                  style={{
                    background: 'rgba(0, 0, 0, 0.02)',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    backdropFilter: 'blur(12px)',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(0, 0, 0, 0.04)';
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(0, 0, 0, 0.02)';
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0, 0, 0, 0.05)';
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(0, 0, 0, 0.04)', color: '#0f172a', border: '1px solid rgba(0, 0, 0, 0.08)' }}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#0f172a] mb-1">
                      {item.text}
                    </div>
                    <div className="text-xs text-[#475569]">
                      {item.sub}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right: Contact form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="lg:col-span-3"
          >
            <div
              className="rounded-3xl p-6 sm:p-8 h-full"
              style={{
                background: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                backdropFilter: 'blur(24px)',
                boxShadow: '0 4px 24px -1px rgba(0, 0, 0, 0.05)',
              }}
            >
              <div className="flex items-center gap-2 mb-8">
                <span className="text-sm font-medium text-[#475569]">Send a message</span>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-xs font-medium mb-2 text-[#475569]"
                    >
                      Your Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onFocus={() => setFocused('name')}
                      onBlur={() => setFocused(null)}
                      required
                      style={inputStyle('name')}
                      placeholder="Amit Adhikary"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-xs font-medium mb-2 text-[#475569]"
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onFocus={() => setFocused('email')}
                      onBlur={() => setFocused(null)}
                      required
                      style={inputStyle('email')}
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-xs font-medium mb-2 text-[#475569]"
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onFocus={() => setFocused('phone')}
                    onBlur={() => setFocused(null)}
                    required
                    style={inputStyle('phone')}
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>

                {/* Message */}
                <div>
                  <label
                    htmlFor="message"
                    className="block text-xs font-medium mb-2 text-[#475569]"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    onFocus={() => setFocused('message')}
                    onBlur={() => setFocused(null)}
                    required
                    rows={4}
                    style={{ ...inputStyle('message'), resize: 'none' }}
                    placeholder="Tell me about your project..."
                  />
                </div>

                {/* Submit */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="relative overflow-hidden w-full flex items-center justify-center gap-2 py-4 rounded-full text-sm font-medium transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    background: 'rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    color: '#0f172a',
                  }}
                  onMouseEnter={e => {
                    if (!isSubmitting) {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(0, 0, 0, 0.08)';
                    }
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(0, 0, 0, 0.05)';
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      <span>Send Message</span>
                    </>
                  )}
                </motion.button>

                {/* Status messages */}
                {submitStatus && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    className="flex items-center gap-3 p-4 rounded-2xl"
                    style={{
                      background: submitStatus === 'success' ? 'rgba(0, 200, 100, 0.1)' : 'rgba(255, 80, 80, 0.1)',
                      border: `1px solid ${submitStatus === 'success' ? 'rgba(0, 200, 100, 0.2)' : 'rgba(255, 80, 80, 0.2)'}`,
                      color: submitStatus === 'success' ? '#0f172a' : '#ff3333',
                    }}
                  >
                    {submitStatus === 'success' ? (
                      <><CheckCircle size={18} className="text-green-600" /><span className="text-sm font-medium">Message sent successfully.</span></>
                    ) : (
                      <><AlertCircle size={18} className="text-red-600" /><span className="text-sm font-medium">Something went wrong. Please try again.</span></>
                    )}
                  </motion.div>
                )}
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;